import { find, get } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';

import { USER_API } from '../../api/user';
import useInfinitePeriodByGroupQuery from '../../hooks/queries/useInfinitePeriodByGroupQuery';
import { getMinutesAsMilliseconds } from '../../utils/numbers';
import { hasCompanyManagerOrAdminRole, hasUserRole } from '../../utils/roles';
import { selectStoreCurrentAuthUser } from '../../utils/storeSelectors';

const useDashboardState = ({ myProfile, timelineFilters }) => {
  const authUser = useSelector(selectStoreCurrentAuthUser);

  const [groupIdFilter, startDateFilter, endDateFilter] = useMemo(
    () => [
      get(timelineFilters, 'groupID'),
      get(timelineFilters, 'startDate'),
      get(timelineFilters, 'endDate'),
    ],
    [timelineFilters],
  );
  const {
    query: groupPeriodsQuery,
    fullyFlatPages: groupPeriodsFlatPages,
  } = useInfinitePeriodByGroupQuery(groupIdFilter, {
    enabled: hasCompanyManagerOrAdminRole(authUser),
  });

  const canFetchDashboardData = useCallback(() => {
    if (
      hasCompanyManagerOrAdminRole(authUser) &&
      timelineFilters.groupID &&
      groupPeriodsQuery.isFetched
    ) {
      return (
        !!find(groupPeriodsFlatPages, { hasAdHocPeriod: true }) ||
        (!!startDateFilter && !!endDateFilter)
      );
    }

    return hasUserRole(authUser) && !!groupIdFilter;
  }, [
    authUser,
    timelineFilters.groupID,
    groupPeriodsQuery.isFetched,
    groupPeriodsFlatPages,
    groupIdFilter,
    startDateFilter,
    endDateFilter,
  ]);

  const dashboardDataQuery = useQuery({
    placeholderData: {},
    staleTime: getMinutesAsMilliseconds(5),
    queryKey: [
      'fetchDashboardData',
      authUser?.profile?._id,
      groupIdFilter,
      startDateFilter,
      endDateFilter,
    ],
    enabled: canFetchDashboardData(),
    queryFn: () =>
      USER_API.fetchDashboardData({
        groupId: groupIdFilter,
        ...(hasCompanyManagerOrAdminRole(authUser) && startDateFilter && endDateFilter
          ? {
              startDate: startDateFilter,
              endDate: endDateFilter,
            }
          : {}),
      }),
  });

  return {
    dashboardDataQuery,
  };
};

export default useDashboardState;
